// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import InputField from 'components/UI/InputField/InputField';
import { SoundType } from 'components/UI/LetterBlock/LetterBlock';
import { BoxTypes } from 'components/UI/StatusBox/StatusBox';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { HomeworkContext } from 'screens/Homework/Homework';
import { deConvertArray } from 'utilities/convertArray';
import { convertString } from 'utilities/convertString';
import { SoundsExerciseContext, UserAnswerEntity } from '../../SoundsExercise';
import { defaultTileBarStateValue } from '../Row/Row';
import './Text.module.scss';
// =========================

export default function Text({
    setAmountOfRows,
    text,
    name,
    tileBarStateAnswer,
    setTriggerAnimation,
    alreadyAnswered,
    category_id,
}: {
    setAmountOfRows?: React.Dispatch<React.SetStateAction<number>>;
    text?: string;
    name?: string;
    tileBarStateAnswer?: SoundType[];
    setTriggerAnimation?: React.Dispatch<React.SetStateAction<boolean>>;
    alreadyAnswered?: UserAnswerEntity;
    category_id?: number;
}) {
    const { sequence } = useContext(HomeworkContext);

    const {
        current,
        words,
        setCurrent,
        answer,
        setUserIsTypingAnswer,
        setUserAnswers,
        useCategories,
        setActiveTileBar,
        setActiveTileBarState,
        useTiles,
        setActiveInput,
        categories,
        activeInput,
    } = useContext(SoundsExerciseContext);
    const [value, setValue] = useState('');
    const [readOnly, setReadOnly] = useState(false);
    const [boxState, setBoxState] = useState<BoxTypes>('initial');
    const [attempts, setAttempts] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const { treatmentId, homeworkId } = useParams() as {
        treatmentId: string;
        homeworkId: string;
    };

    // set box values when answer is already given
    useEffect(() => {
        if (alreadyAnswered) {
            setValue(alreadyAnswered.correctText);
            setReadOnly(true);
            if (alreadyAnswered.correctText === alreadyAnswered.text)
                return setBoxState('correct');
            setBoxState('failed');
            return;
        }
        setValue('');
        setBoxState('initial');
        setAttempts(0);
        setReadOnly(false);
    }, [alreadyAnswered, name]); // name is added here to fix bug when user has slow network connection and alreadyAnswered would not exists yet when the user goes to next word. In that case the this useeffect would not fire. That caused the exercise to get stuck because this component would not reset to its initial state

    useEffect(() => {
        if (useCategories && activeInput === 'initial' && !useTiles) return;
        if (name === answer?.name) return inputRef?.current?.focus();

        if (!text && !readOnly) inputRef?.current?.focus();
    }, [answer]);

    function storeWrong() {
        dispatch(
            storeWrongAnswer({
                given_answer: value,
                correct_answer: answer?.name,
                metadata: {
                    sequence,
                },
                type: 'word',
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
        );
    }

    function finish(state: BoxTypes) {
        if (useCategories && current < words.length - 1) {
            // timeout is to make sure that the current equals the userAnswers array in SoundsExercise.tsx
            setTimeout(() => {
                setCurrent((prev) => prev + 1);
            }, 100);
            setAmountOfRows!((prev) => prev + 1);
        }
        if (useTiles) {
            setUserAnswers((prev) => [
                ...prev,
                {
                    bars: tileBarStateAnswer!,
                    text: value,
                    id: answer.id,
                    correctBars: deConvertArray(answer?.klanken),
                    correctText: answer.name,
                    category_id,
                },
            ]);
        } else {
            setUserAnswers((prev) => [
                ...prev,
                {
                    bars: null,
                    text: value,
                    id: answer.id,
                    correctBars: [],
                    correctText: answer.name,
                    category_id,
                },
            ]);
        }

        if (useCategories && !useTiles) {
            setActiveInput('initial');
        }

        setBoxState(state);
        setUserIsTypingAnswer(false);
        setReadOnly(true);
        setActiveTileBar('initial');
        setActiveTileBarState(defaultTileBarStateValue);
        if (state === 'wrong') {
            storeWrong();
            setValue(answer?.name);
            setBoxState('failed');
        }
    }

    function tryAgain() {
        storeWrong();
        setReadOnly(true);
        setBoxState('wrong');
        setTimeout(() => {
            setBoxState('submit');
            setReadOnly(false);
        }, 1500);
    }

    function handleSubmit(e?: React.FormEvent<HTMLFormElement>) {
        e?.preventDefault();
        if (readOnly || !value) return;

        if (convertString(value) === convertString(answer?.name))
            finish('correct');
        else setAttempts((prev) => prev + 1);
    }

    function handleInputClick() {
        if (boxState === 'correct' || boxState === 'failed') return;
        if (useCategories && !useTiles) {
            const catWords = categories
                .map((c) => c.name === name && c.words)
                .filter((exists) => exists)[0];

            if (
                catWords &&
                catWords?.find((word) => word?.name === answer?.name)
            ) {
                setActiveInput(name!);
                setReadOnly(false);
            } else if (setTriggerAnimation) {
                setTriggerAnimation(true);
            }
        }
    }

    useEffect(() => {
        if (useCategories && !useTiles) setReadOnly(true);
    }, []);

    useEffect(() => {
        if (attempts === 0) return;
        if (attempts === 3) finish('wrong');
        else tryAgain();
    }, [attempts]);

    const active = !useCategories ? name === answer?.name : !!useCategories;

    return (
        <InputField
            boxState={boxState}
            setBoxState={setBoxState}
            readOnly={readOnly}
            value={value}
            setValue={setValue}
            handleSubmit={handleSubmit}
            text={text}
            reference={inputRef}
            active={active}
            handleInputClick={handleInputClick}
        />
    );
}
